import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';

import { FilterSelectionOption } from '../../models';
import { getVisibleOptions, sortItemsBySelection } from '../../utils';
import { FilterBadgeComponent } from '../filter-badge/filter-badge.component';
import { FilterClearButtonComponent } from '../filter-clear-button/filter-clear-button.component';
import { FilterGroupComponent } from '../filter-group/filter-group.component';
import { MultiSelectionFilterComponent } from '../multi-selection-filter/multi-selection-filter.component';
import { ShowAllOptionsButtonComponent } from '../show-all-options-button/show-all-options-button.component';

@Component({
  selector: 'mp-multi-selection-group',
  templateUrl: './multi-selection-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FilterGroupComponent,
    FilterBadgeComponent,
    FilterClearButtonComponent,
    MultiSelectionFilterComponent,
    ShowAllOptionsButtonComponent,
  ],
})
export class MultiSelectionGroupComponent<T> {
  readonly options: InputSignal<FilterSelectionOption<T>[]> = input.required<FilterSelectionOption<T>[]>();
  readonly selectedValues: ModelSignal<T[]> = model<T[]>([]);

  readonly filterName: InputSignal<string> = input.required<string>();
  readonly icon: InputSignal<string | undefined> = input<string>();

  readonly isExpanded: ModelSignal<boolean> = model<boolean>(true);

  readonly searchable: InputSignal<boolean> = input<boolean>(false);
  readonly searchFieldPlaceholder: InputSignal<string> = input<string>('Durchsuchen');

  readonly showSelectionInfo: InputSignal<boolean> = input<boolean>(false);
  readonly sortOptionsBySelection: InputSignal<boolean> = input<boolean>(false);
  readonly maxVisibleItems: InputSignal<number | null> = input<number | null>(null);

  readonly optionSelect: OutputEmitterRef<FilterSelectionOption<T>> = output<FilterSelectionOption<T>>();

  protected readonly searchTerm: WritableSignal<string> = signal<string>('');

  protected readonly showAll: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly selectionCount: Signal<number> = computed(() => this.selectedValues()?.length ?? 0);

  protected readonly filteredOptions: Signal<FilterSelectionOption<T>[]> = computed(() => {
    const filteredOptions = this.filterOptionsBySearchTerm(this.options(), this.searchTerm());
    return this.sortOptionsBySelection()
      ? sortItemsBySelection(this.selectedValues(), filteredOptions)
      : filteredOptions;
  });

  protected readonly visibleOptions: Signal<FilterSelectionOption<T>[]> = computed(() =>
    getVisibleOptions(this.filteredOptions(), this.maxVisibleItems(), this.showAll()),
  );

  filterOptionsBySearchTerm(options: FilterSelectionOption<T>[], searchTerm: string): FilterSelectionOption<T>[] {
    if (!searchTerm) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  onSelectionChanged(selectedValues: T[]): void {
    this.selectedValues.set(selectedValues);
  }

  onOptionSelected(selectionOption: FilterSelectionOption<T>): void {
    this.optionSelect.emit(selectionOption);
  }

  clearSelection(): void {
    this.selectedValues.set([]);
  }
}
